import type { FleetMachineOverview } from '@immo/api-contract';
import { Label, NativeSelect } from '@immo/ui-kit';
import { CommitOnBlurInput } from '@/components/commit-on-blur-input';
import { useTranslations } from '@/i18n/use-translations';
import { customDays, HISTORY_RANGES, isDayKey, type HistoryRange } from '@/lib/fleet/history-range';
import type { ListParamChanges } from '@/lib/list-view/use-list-params';
import { Segments } from '../segments';

type Props = {
  readonly machines: ReadonlyArray<FleetMachineOverview>;
  readonly selection: {
    readonly machineId: string;
    readonly range: HistoryRange;
    readonly from: string | null;
    readonly to: string | null;
  };
  readonly today: string;
  readonly onChange: (changes: ListParamChanges) => void;
};

/** The machine, the period and, for a custom period, its two days (IMM-1450). */
export function HistoryFilters({ machines, selection, today, onChange }: Props) {
  const t = useTranslations('pages.admin.fleet-history');

  function chooseRange(range: HistoryRange) {
    if (range !== 'custom') return onChange({ range, from: null, to: null });

    const days = customDays(null, null, today);

    onChange({ range, from: days.from, to: days.to });
  }

  // An unreadable day is ignored: the range stays as it was.
  function commitFrom(next: string) {
    if (isDayKey(next)) onChange(customDays(next, selection.to, today));
  }

  function commitTo(next: string) {
    if (isDayKey(next)) onChange(customDays(selection.from, next, today));
  }

  const from = selection.from ?? '';

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
      <div className="flex flex-col gap-1">
        <Label htmlFor="history-machine" className="text-sm text-text-tertiary">
          {t('machine')}
        </Label>
        <NativeSelect
          id="history-machine"
          size="sm"
          value={selection.machineId}
          options={machines.map((machine) => ({ value: machine.id, label: machine.name }))}
          onChange={(machineId) => onChange({ machine: machineId })}
        />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-sm text-text-tertiary">{t('range')}</span>
        <Segments<HistoryRange>
          ariaLabel={t('range')}
          options={HISTORY_RANGES.map((range) => ({ value: range, label: t(`range-${range}`) }))}
          value={selection.range}
          onChange={chooseRange}
        />
      </div>
      {selection.range === 'custom' && (
        <>
          <div className="flex flex-col gap-1">
            <Label htmlFor="history-from" className="text-sm text-text-tertiary">
              {t('from')}
            </Label>
            <CommitOnBlurInput
              id="history-from"
              type="date"
              className="h-8"
              max={today}
              value={from}
              onCommit={commitFrom}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="history-to" className="text-sm text-text-tertiary">
              {t('to')}
            </Label>
            <CommitOnBlurInput
              id="history-to"
              type="date"
              className="h-8"
              min={from}
              max={today}
              value={selection.to ?? ''}
              onCommit={commitTo}
            />
          </div>
        </>
      )}
    </div>
  );
}
